import { computed, ComputedRef, ref } from 'vue'
import { EnvFeaturesEnum, FeatureType, useFeatureFlip } from '@invivodf/front-seller-feature-flipping'
import { useDiscountAssociationParameters } from '@invivodf/common/src/contexts/discount'
import {
  ParametersDescription,
  SectionName,
  useParametersFormatter,
  useTransversalParameters,
} from '@invivodf/common/src/contexts/parameters'
import { AppContext } from '@invivodf/common/app.context'
import { capitalize } from 'lodash'
import { useI18nForVue2 } from '@invivodf/locales/src/useI18n'
import { useStore } from '@/composables/useStore'

/**
 * Cette fonction devrait importer les différents paramètres de chaque contextes.
 * Chaque paramètre devrait donc être définit dans des fonctions de compositions exportées par les contextes fonctionnels correspondants.
 * Ce travail n'a pas encore était fait car beaucoup des contexts auxquels appartiennent ces paramètres n'existent pas.
 */
export const useParameters = () => {
  const i18n = useI18nForVue2()
  const store = useStore()
  const { cooperativeId } = store.getters

  const importVariant = computed(() => ({
    title: `${i18n.t('ctx.parameters.product__import-variants__title')}`,
    description: `${i18n.t('ctx.parameters.product__import-variants__description')}`,
    route: 'import-variants',
    icon: 'book-outlined',
    section: SectionName.Product,
  }))

  const isVariantsSegmentationEnabled = useFeatureFlip({
    id: 'variants-segmentations',
    type: FeatureType.Dynamic,
  })

  const importVariantSegmentations = computed(() => ({
    title: `${i18n.t('ctx.parameters.product__import-variant-segmentations__title')}`,
    description: `${i18n.t('ctx.parameters.product__import-variant-segmentations__description')}`,
    route: 'import-variant-segmentations',
    icon: 'people-outlined',
    enableFeature: isVariantsSegmentationEnabled.value,
    section: SectionName.Product,
  }))

  const importOffers = computed(() => ({
    title: `${i18n.t('ctx.parameters.product__import-offers__title')}`,
    description: `${i18n.t('ctx.parameters.product__import-offers__description')}`,
    route: 'import-offers',
    icon: 'offer-outlined',
    section: SectionName.Product,
  }))

  const importProductAdditionalData = computed(() => ({
    title: `${i18n.t('ctx.parameters.product__import-products-additional-data__title')}`,
    description: `${i18n.t('ctx.parameters.product__import-products-additional-data__description')}`,
    route: 'import-products-additional-data',
    icon: 'star-filled',
    section: SectionName.Product,
  }))

  const usersParameters = [
    computed(() => ({
      title: capitalize(i18n.t('message.memberships').toString()),
      description: `${i18n.t('ctx.parameters.users__import-users__description-1')} ${i18n.t('message.memberships')} ${i18n.t('ctx.parameters.users__import-users__description-2')} ${i18n.t(
        'message.yourPartner',
      )} ${i18n.t('ctx.parameters.users__import-users__description-3')}`,
      route: 'import-members',
      icon: 'b2b-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: i18n.t('message.technicians').toString(),
      description: `${i18n.t('ctx.parameters.users__import-users__description-1')} ${i18n.t('message.technicians')} ${i18n.t('ctx.parameters.users__import-users__description-2')} ${i18n.t('message.yourPartner')}`,
      route: 'import-technical-sales',
      icon: 'b2c-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__delivery-addresses__title')}`,
      description: `${i18n.t('ctx.parameters.users__delivery-addresses__description')} ${i18n.t('message.memberships')}`,
      route: 'import-shipping-addresses',
      icon: 'location-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__electronic-invoicing-addresses__title')}`,
      description: `${i18n.t('ctx.parameters.users__electronic-invoicing-addresses__description')} ${i18n.t('message.memberships')}`,
      route: 'import-electronic-invoicing-addresses',
      icon: 'file-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__grouping-addresses__title')}`,
      description: `${i18n.t('ctx.parameters.users__grouping-addresses__description')} ${i18n.t('message.memberships')}`,
      route: 'import-grouping-addresses',
      icon: 'location-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__segmentation__title')} ${i18n.t('message.memberships')}`,
      description: `${i18n.t('ctx.parameters.users__segmentation__description-1')} ${i18n.t('message.memberships')} ${i18n.t('ctx.parameters.users__segmentation__description-2')}`,
      route: 'import-segments',
      icon: 'people-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: capitalize(i18n.t('message.partner-contact.import-title').toString()),
      description: `${i18n.t('ctx.parameters.users__import-users__description-1')} ${i18n.t('message.partner-contact.contacts')} ${i18n.t('ctx.parameters.users__import-users__description-2')} ${i18n.t(
        'message.yourPartner',
      )}`,
      route: 'import-contacts',
      icon: 'b2b-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: capitalize(i18n.t('customer-store.import-title').toString()),
      description: `${i18n.t('customer-store.description')}`,
      route: 'import-store-customer',
      icon: 'store-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__import-users-roles__title')}`,
      description: `${i18n.t('ctx.parameters.users__import-users-roles__description')}`,
      route: 'import-users-roles',
      icon: 'b2b-outlined',
      section: SectionName.Users,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.users__import-sales-stores__title')}`,
      description: `${i18n.t('ctx.parameters.users__import-sales-stores__description')}`,
      route: 'import-sales-stores',
      icon: 'store-outlined',
      section: SectionName.Users,
    })),
  ]

  const paymentParameters = [
    computed(() => ({
      title: `${i18n.t('ctx.parameters.payment__title')}`,
      description: `${i18n.t('ctx.parameters.payment__description')} ${i18n.t('message.frontAgri')}`,
      route: 'details-paiements-cb',
      icon: 'card-outlined',
      section: SectionName.Payment,
    })),
  ]

  const validationParameters = [
    computed(() => ({
      title: `${i18n.t('ctx.parameters.order-validation__title')}`,
      description: `${i18n.t('ctx.parameters.order-validation__description')}`,
      route: 'order-validation',
      icon: 'hand-thumb-up-filled',
      section: SectionName.Validation,
    })),
  ]

  const deliveryParameters = [
    computed(() => ({
      title: `${i18n.t('ctx.parameters.delivery__storage-sites__title')}`,
      description: `${i18n.t('ctx.parameters.delivery__storage-sites__description')}`,
      route: 'import-stores',
      icon: 'store-outlined',
      section: SectionName.Delivery,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.delivery__delivery-zones__title')}`,
      description: `${i18n.t('ctx.parameters.delivery__delivery-zones__description')}`,
      route: 'zone-charges',
      icon: 'truck-outlined',
      section: SectionName.Delivery,
    })),
  ]

  const isCustomFiltersActive = useFeatureFlip({
    id: 'custom-filters-configuration',
    type: FeatureType.Dynamic,
    context: ref({
      id: cooperativeId.toString(),
    }),
  })

  const marketingParameters: ComputedRef<ParametersDescription>[] = []
  if (window.env.CONTENTFUL_ARTICLES_BANNER_EDITION_FEATURE === 'yes') {
    marketingParameters.push(
      computed(() => ({
        title: `${i18n.t('ctx.parameters.marketing__articles-news__title')}`,
        route: 'articles',
        icon: 'file-outlined',
        section: SectionName.Marketing,
      })),
    )
    marketingParameters.push(
      computed(() => ({
        title: `${i18n.t('ctx.parameters.marketing__home-banner__title')}`,
        route: 'partner-banner',
        icon: 'profile-card-outlined',
        section: SectionName.Marketing,
      })),
    )
  }
  marketingParameters.push(
    computed(() => ({
      title: `${i18n.t('ctx.parameters.product__custom-filters__title')}`,
      route: 'custom-filters',
      icon: 'filter-outlined',
      enableFeature: isCustomFiltersActive.value,
      section: SectionName.Marketing,
    })),
  )

  const isVirtualCategoriesEnabled = useFeatureFlip({
    id: 'front:seller:import-virtual-categories',
    type: FeatureType.Dynamic,
  })

  marketingParameters.push(
    computed(() => ({
      title: `${i18n.t('ctx.parameters.virtual-categories__title')}`,
      description: `${i18n.t('ctx.parameters.virtual-categories__description')}`,
      route: 'virtual-categories',
      icon: 'dashboard-outlined',
      enableFeature: isVirtualCategoriesEnabled.value,
      section: SectionName.Marketing,
    })),
  )

  const isImportPaymentDueDatesActive = useFeatureFlip({
    id: EnvFeaturesEnum.PAYMENT_DUE_DATES_FEATURE,
    type: FeatureType.Env,
  })

  const importPaymentDueDates = computed(() => ({
    title: `${i18n.t('ctx.parameters.product__import-payment-due-dates__title')}`,
    description: `${i18n.t('ctx.parameters.product__import-payment-due-dates__description')}`,
    route: 'payment-due-dates',
    icon: 'offer-outlined',
    enableFeature: isImportPaymentDueDatesActive.value,
    section: SectionName.Product,
  }))

  const productParameters = [
    importVariant,
    importVariantSegmentations,
    importPaymentDueDates,
    importProductAdditionalData,
  ]

  const offerParameters = [importOffers]

  const stockParameters = [
    computed(() => ({
      title: `${i18n.t('ctx.parameters.stock-and-quota__import-stock__title')}`,
      description: `${i18n.t('ctx.parameters.stock-and-quota__import-stock__description')}`,
      route: 'import-stock',
      icon: 'upload',
      section: SectionName.StockAndQuota,
    })),
    computed(() => ({
      title: `${i18n.t('ctx.parameters.stock-and-quota__stock-threshold__title')}`,
      description: `${i18n.t('ctx.parameters.stock-and-quota__stock-threshold__description')}`,
      route: 'stock-thresholds',
      icon: 'upload',
      enableFeature: false,
      section: SectionName.StockAndQuota,
    })),
  ]

  const isImportQuotaActive = useFeatureFlip({
    id: EnvFeaturesEnum.IMPORT_QUOTA_FEATURE,
    type: FeatureType.Env,
  })
  const quotaParameters = [
    computed(() => ({
      title: `${i18n.t('ctx.parameters.stock-and-quota__import-quota__title')}`,
      description: `${i18n.t('ctx.parameters.stock-and-quota__import-quota__description')}`,
      route: 'import-quotas',
      icon: 'upload',
      enableFeature: isImportQuotaActive.value,
      section: SectionName.StockAndQuota,
    })),
  ]

  const transversalParameters = useTransversalParameters()
  const discountParameters = useDiscountAssociationParameters()

  const sections = computed(() => {
    return useParametersFormatter([
      ...transversalParameters,
      ...discountParameters,
      ...productParameters,
      ...offerParameters,
      ...stockParameters,
      ...quotaParameters,
      ...usersParameters,
      ...(window.env.VUE_APP_CONTEXT !== AppContext.episens ? paymentParameters : []),
      ...validationParameters,
      ...deliveryParameters,
      ...marketingParameters,
    ])
  })

  return { sections }
}
